import { PersonIdentity } from '@/components/PersonIdentity'
import { inputClear } from '@/components/inputClear'
import { useMemo, useState, type PropsWithChildren } from 'react'
import { Link, useLocation } from 'wouter'
import { Alert, Button, Descriptions, Drawer, Dropdown, Empty, Input, Layout, List, Menu, Modal, Space, Switch, Tag, Tooltip, Typography } from 'antd'
import { ArrowRightOutlined, LogoutOutlined, MenuOutlined, QuestionCircleOutlined, ReadOutlined, SearchOutlined, UserOutlined } from '@ant-design/icons'
import { buildMenuItems, navigationGroupByPath } from '@/layout/navigation'
import { useAuth } from '@/auth/AuthContext'
import { appRoutes } from '@/app/routes'
import { BrandLogo } from '@/components/BrandLogo'

const { Sider, Header, Content } = Layout

export function AdminShell({ children }: PropsWithChildren) {
  const [pathname, navigate] = useLocation()
  const { user, logout, can } = useAuth()
  const menuItems = useMemo(() => buildMenuItems(can), [can])
  const [searchOpen, setSearchOpen] = useState(false)
  const [searchKeyword, setSearchKeyword] = useState('')
  const [mobileOpen, setMobileOpen] = useState(false)
  const [helpOpen, setHelpOpen] = useState(false)
  const [accessibilityOpen, setAccessibilityOpen] = useState(false)
  const [profileOpen, setProfileOpen] = useState(false)
  const [largeText, setLargeText] = useState(false)
  const [highContrast, setHighContrast] = useState(false)
  const [collapsed, setCollapsed] = useState(false)
  const basePath = Object.keys(navigationGroupByPath).find((path) => pathname === path || pathname.startsWith(`${path}/`))
  const currentGroup = basePath ? navigationGroupByPath[basePath]?.key : undefined
  const [openKeys, setOpenKeys] = useState<string[]>(currentGroup ? [currentGroup] : [])
  const effectiveOpenKeys = currentGroup && !openKeys.includes(currentGroup) ? [currentGroup] : openKeys
  const selectedKey = useMemo(() => {
    const exact = appRoutes.find((item) => item.path === pathname)
    if (exact) return exact.path
    return appRoutes.find((item) => item.path.includes('/:') && pathname.startsWith(item.path.split('/:')[0]))?.path.split('/:')[0] ?? '/'
  }, [pathname])
  const searchableRoutes = useMemo(() => {
    const keyword = searchKeyword.trim().toLowerCase()
    return appRoutes.filter((route) => !route.path.includes('/:') && can(route.permission) && (!keyword || `${route.title} ${route.path}`.toLowerCase().includes(keyword)))
  }, [can, searchKeyword])

  const navigation = (
    <Menu
      mode="inline"
      items={menuItems}
      selectedKeys={[selectedKey]}
      openKeys={effectiveOpenKeys}
      onOpenChange={(keys) => setOpenKeys(keys.map(String))}
      onClick={({ key }) => {
        if (key.startsWith('/')) navigate(key)
        setMobileOpen(false)
      }}
      aria-label="管理端主导航"
      className="admin-menu"
    />
  )

  return (
    <Layout className="admin-layout">
      <a className="skip-link" href="#main-content">跳到主要内容</a>
      <Sider width={224} collapsedWidth={64} collapsible collapsed={collapsed} onCollapse={setCollapsed} className="admin-sider" theme="light" trigger={null}>
        <div className="admin-brand"><BrandLogo /></div>
        {navigation}
        <Button className="sider-collapse" type="text" icon={<MenuOutlined aria-hidden="true" />} aria-label={collapsed ? '展开主导航' : '收起主导航'} onClick={() => setCollapsed((value) => !value)}>{collapsed ? null : '收起菜单'}</Button>
      </Sider>
      <Layout className={`admin-content${collapsed ? ' admin-content--collapsed' : ''}`}>
        <Header className="admin-header">
          <Space>
            <Button className="mobile-sider-trigger" icon={<MenuOutlined aria-hidden="true" />} aria-label="打开主导航" onClick={() => setMobileOpen(true)} />
            <Tooltip title="搜索管理功能"><Button aria-label="搜索管理功能" className="header-search" icon={<SearchOutlined aria-hidden="true" />} onClick={() => setSearchOpen(true)} aria-haspopup="dialog">搜索管理功能</Button></Tooltip>
          </Space>
          <div className="header-actions">
            <Tooltip title="帮助"><Button aria-label="帮助" type="text" icon={<QuestionCircleOutlined aria-hidden="true" />} onClick={() => setHelpOpen(true)}>帮助</Button></Tooltip>
            <Tooltip title="无障碍设置"><Button aria-label="无障碍设置" type="text" icon={<ReadOutlined aria-hidden="true" />} onClick={() => setAccessibilityOpen(true)}>无障碍设置</Button></Tooltip>
            <Dropdown menu={{ onClick: ({ key }) => {
              if (key === 'profile') setProfileOpen(true)
              if (key === 'logout') void logout().then(() => navigate('/login'))
            }, items: [
              { key: 'profile', icon: <UserOutlined aria-hidden="true" />, label: '账户信息' },
              { type: 'divider' },
              { key: 'logout', icon: <LogoutOutlined aria-hidden="true" />, label: '退出登录' },
            ] }}>
              <Button type="text" aria-label={`账户菜单：${user?.name ?? ''}`}><PersonIdentity id={user?.id} name={user?.name} size={28} /></Button>
            </Dropdown>
          </div>
        </Header>
        <Content id="main-content" className="admin-main" tabIndex={-1}>{children}</Content>
      </Layout>

      <Modal title="搜索管理功能" open={searchOpen} onCancel={() => { setSearchOpen(false); setSearchKeyword('') }} footer={null} afterOpenChange={(open) => open && document.getElementById('global-search')?.focus()}>
        <Input id="global-search" size="large" prefix={<SearchOutlined aria-hidden="true" />} placeholder="输入页面或功能名称" value={searchKeyword} onChange={(event) => setSearchKeyword(event.target.value)} allowClear={inputClear} />
        <List
          className="global-search-results"
          dataSource={searchableRoutes}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="没有匹配的管理功能" /> }}
          renderItem={(route) => <List.Item><Link className="global-search-result" href={route.path} onClick={() => { setSearchOpen(false); setSearchKeyword('') }}><span>{route.title}</span><ArrowRightOutlined aria-hidden="true" /></Link></List.Item>}
        />
      </Modal>
      <Drawer title={<BrandLogo />} placement="left" open={mobileOpen} onClose={() => setMobileOpen(false)} width={360}>{navigation}</Drawer>
      <Modal title="账户信息" open={profileOpen} onCancel={() => setProfileOpen(false)} footer={<Button type="primary" onClick={() => setProfileOpen(false)}>关闭</Button>}>
        <Descriptions column={1} bordered size="small" items={[
          { key: 'name', label: '账户名称', children: <PersonIdentity id={user?.id} name={user?.name} showNumber /> },
          { key: 'role', label: '管理角色', children: user?.roleName ?? '—' },
          { key: 'permissions', label: '权限数量', children: <Tag color="blue">{user?.permissions.length ?? 0} 项</Tag> },
        ]} />
      </Modal>
      <Modal title="管理后台帮助" open={helpOpen} onCancel={() => setHelpOpen(false)} footer={<Button type="primary" onClick={() => setHelpOpen(false)}>知道了</Button>}><Typography.Paragraph>页面操作会按当前管理员权限显示；审核、冻结、下架和付款确认等操作均会再次校验权限并记录操作审计。</Typography.Paragraph><Alert type="info" showIcon title="遇到加载或操作失败" description="先刷新相关列表并在“操作审计”中确认结果；结果不明确时不要重复执行高影响操作。" /></Modal>
      <Modal title="无障碍设置" open={accessibilityOpen} onCancel={() => setAccessibilityOpen(false)} footer={<Button type="primary" onClick={() => setAccessibilityOpen(false)}>完成</Button>}><div className="accessibility-options"><label><span><strong>大号界面文字</strong><small>将界面基础文字提升一级，不影响浏览器缩放。</small></span><Switch checked={largeText} checkedChildren="开" unCheckedChildren="关" onChange={(checked) => { setLargeText(checked); document.documentElement.classList.toggle('admin-large-text', checked) }} /></label><label><span><strong>增强对比度</strong><small>增强边框和焦点可见性；系统强制色仍优先。</small></span><Switch checked={highContrast} checkedChildren="开" unCheckedChildren="关" onChange={(checked) => { setHighContrast(checked); document.documentElement.classList.toggle('admin-high-contrast', checked) }} /></label></div><Typography.Paragraph type="secondary" className="section-spacing">也可以使用浏览器页面缩放、系统高对比度和读屏软件，本设置不会覆盖系统偏好。</Typography.Paragraph></Modal>
    </Layout>
  )
}
